import { useEffect, useState } from "react";
import type { PropsWithChildren } from "react";
import type { ChangeHandler } from "react-hook-form";

import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { LoginModalView, languageSelector, useActiveLoginModalContext } from "@/src/contexts";
import LoginResources from "@/src/modals/login-registration/Login.Resources";
import LoginResourcesEn from "@/src/modals/login-registration/Login.Resources.En";
import LoginAndRegistrationForm from "@/src/modals/login-registration/LoginAndRegistrationForm";
import RegistrationResources from "@/src/modals/login-registration/Registration.Resources";
import RegistrationResourcesEn from "@/src/modals/login-registration/Registration.Resources.En";
import SocialLogin from "@/src/modals/login-registration/SocialLogin";
import { type ConsentScreenType, type IConsentItem, useConsents } from "@/src/modals/useConsents";
import ButtonV2 from "@Components/Button/ButtonV2";
import { useDeviceFingerprint } from "@Components/ZeroFraud";

import * as css from "./login-registration-core.module.css";

export function LoginRegistrationCore({
    loginConsentScreenType,
    registerConsentScreenType,
    viewTelemetry,
    customRegisterButtonTelemetry,
    getIsRegistration,
    onSuccess,
}: PropsWithChildren<{
    loginConsentScreenType: ConsentScreenType;
    registerConsentScreenType: ConsentScreenType;
    viewTelemetry?: Partial<ITelemetryCustom>;
    customRegisterButtonTelemetry?:ITelemetryCustom;
    getIsRegistration?: (isRegistration: boolean) => void;
    onSuccess?: () => void;
}>) {
    const { setActiveLoginModal, reCaptchaPublicToken } = useActiveLoginModalContext();
    const [isRegistration, setIsRegistration] = useState(true);
    const fingerprint = useDeviceFingerprint();
    const [updatedConsents, setUpdatedConsents] = useState<IConsentItem[]>([]);

    const resources = languageSelector(
        isRegistration ? RegistrationResources : LoginResources,
        isRegistration ? RegistrationResourcesEn : LoginResourcesEn,
    );

    const consentScreenType = isRegistration ? registerConsentScreenType : loginConsentScreenType;
    const consents = useConsents(consentScreenType);

    useEffect(() => {
        sendTelemetryEvent({
            ...viewTelemetry,
            eventName: `common-${isRegistration ? "register" : "login"}`,
            actionType: "View",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRegistration]);

    useEffect(() => {
        setUpdatedConsents(consents);
    }, [consents]);

    const onConsentStatusChange: ChangeHandler = (event) => {
        setUpdatedConsents(
            consents.map((value) => {
                const checkbox = event.target as HTMLInputElement;
                if (checkbox.id.includes(value.id)) {
                    value.isSelectedByDefault = checkbox.checked;
                }

                return value;
            }),
        );

        return Promise.resolve();
    };

    return (
        <div className={css.loginRegistrationCoreComponent} >
            <LoginAndRegistrationForm
                formTitle={resources.formTitle}
                consents={updatedConsents}
                reCaptchaPublicToken={reCaptchaPublicToken}
                resources={resources}
                isRegistration={isRegistration}
                consentScreenType={consentScreenType}
                onConsentStatusChange={onConsentStatusChange}
                forgetPasswordOpen={() => setActiveLoginModal(LoginModalView.ForgetPassword)}
                bypassOnboardingScreen
                fingerprint={fingerprint}
                onSuccess={onSuccess}
                customRegisterButtonTelemetry={customRegisterButtonTelemetry}
            />
            <div className={css.separator}>
                <hr />
                {resources.socialButtonsTitle}
                <hr />
            </div>
            <SocialLogin
                isRegistration={isRegistration}
                consents={updatedConsents}
                consentScreenType={consentScreenType}
                formSubmitText={resources.formButton}
                consentErrorMessage={resources.userConsentError}
                showLessText={resources.showLess}
                showMoreText={resources.showMore}
                modalConfirmationButton={resources.modalConfirmationButton}
                onConsentStatusChange={onConsentStatusChange}
            />
            <div className={css.contextSwitcher}>
                <p>{resources.changeScreenButtonText}</p>
                <ButtonV2
                    appearance="transparentMagentaAppearance"
                    type="button"
                    onClick={() => {
                        setIsRegistration(!isRegistration);
                        getIsRegistration?.(isRegistration);
                    }}
                >
                    <b>{resources.changeScreenButtonLink}</b>
                </ButtonV2>
            </div>
        </div>
    );
}
